import React from 'react'
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import {
    Lesson,
    Question,
    QuestionSlide,
    MultipleChoiceQuestion,
    FillBlankQuestion,
    ShortAnswerQuestion,
} from '@all-for-one/shared-types'

// ── Public types ──────────────────────────────────────────────────────────────

export interface WorksheetEntry {
    level: string
    sections: string[]
}

export interface PdfBaseProps {
    lesson: Lesson
    assignedClassName?: string
    gradeBand?: string
    subject?: string
    logoDataUrl: string
    downloadDate: string
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 11,
        color: '#222222',
        paddingTop: 72,
        paddingBottom: 54,
        paddingLeft: 54,
        paddingRight: 54,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 54,
        right: 54,
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: 8,
        color: '#999999',
    },
    // Document header (draft / answer key)
    docHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 2,
        borderBottomColor: '#333333',
        borderBottomStyle: 'solid',
        paddingBottom: 8,
        marginBottom: 14,
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    logo: { height: 36 },
    lessonName: { fontFamily: 'Helvetica-Bold', fontSize: 10 },
    headerDetail: { fontSize: 8, color: '#666666', marginTop: 1 },
    badgeDraft: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        paddingVertical: 5,
        paddingHorizontal: 9,
        backgroundColor: '#fef3cd',
        color: '#856404',
        borderRadius: 4,
    },
    badgeAnswerKey: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        paddingVertical: 5,
        paddingHorizontal: 9,
        backgroundColor: '#e2e3f1',
        color: '#12247c',
        borderRadius: 4,
    },
    // Section / level headers
    sectionHeader: {
        fontSize: 13,
        fontFamily: 'Helvetica-Bold',
        marginTop: 18,
        marginBottom: 10,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: '#999999',
        borderBottomStyle: 'solid',
    },
    levelHeader: {
        fontSize: 11,
        fontFamily: 'Helvetica-BoldOblique',
        color: '#444444',
        marginTop: 8,
        marginBottom: 6,
    },
    // Questions (draft / answer key)
    question: { marginBottom: 14 },
    // Questions (worksheet — doubled spacing)
    questionWs: { marginBottom: 48 },
    questionRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 3,
        marginBottom: 4,
    },
    questionLabel: { fontFamily: 'Courier-Bold', fontSize: 10 },
    questionText: { fontSize: 11, flexShrink: 1 },
    answerText: {
        fontFamily: 'Helvetica-Bold',
        color: '#12247c',
        marginTop: 3,
        marginLeft: 20,
        fontSize: 11,
    },
    slideNote: {
        fontSize: 8,
        color: '#888888',
        fontFamily: 'Helvetica-Oblique',
        marginBottom: 2,
    },
    questionImage: {
        alignSelf: 'center',
        maxWidth: '65%',
        maxHeight: 140,
        objectFit: 'contain',
        marginVertical: 6,
    },
    // Multiple choice — horizontal for short options, vertical for long
    mcOptionsRow: {
        flexDirection: 'row',
        gap: 8,
        marginTop: 6,
    },
    mcOptionsCol: {
        flexDirection: 'column',
        gap: 4,
        marginTop: 6,
        marginLeft: 16,
    },
    mcOptionFlex: { flex: 1 },
    mcOptionFull: {},
    mcOptionImage: {
        alignSelf: 'center',
        maxWidth: '100%',
        maxHeight: 60,
        objectFit: 'contain',
        marginBottom: 4,
    },
    mcOptionRow: {
        flexDirection: 'row',
        gap: 2,
        justifyContent: 'flex-start',
    },
    mcLetter: { fontFamily: 'Helvetica-Bold', minWidth: 14 },
    // Fill blank
    fillBlankRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        marginTop: 4,
        gap: 2,
    },
    blankLine: {
        width: 169,   // 225px × (72pt/96dpi) ≈ 169pt
        borderBottomWidth: 1.5,
        borderBottomColor: '#333333',
        borderBottomStyle: 'solid',
        marginHorizontal: 3,
        height: 14,
    },
    // Short answer
    shortAnswerLines: { marginTop: 6, marginLeft: 16, gap: 2 },
    ruledLine: {
        borderBottomWidth: 1,
        borderBottomColor: '#999999',
        borderBottomStyle: 'solid',
        height: 18,
    },
    // Directions box (worksheets)
    directions: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#dee2e6',
        borderStyle: 'solid',
        borderRadius: 3,
        padding: 8,
        marginBottom: 10,
        marginTop: 4,
    },
    directionLine: { fontSize: 9, color: '#555555', marginVertical: 1 },
    // Worksheet header
    wsTopRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: 16,
    },
    wsNameRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 4 },
    wsNameLabel: { fontSize: 11 },
    wsNameBlank: {
        width: 180,
        borderBottomWidth: 1,
        borderBottomColor: '#333333',
        borderBottomStyle: 'solid',
        height: 14,
    },
    wsDocHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        borderBottomWidth: 2,
        borderBottomColor: '#333333',
        borderBottomStyle: 'solid',
        paddingBottom: 8,
        marginBottom: 10,
    },
})

// ── Footer ────────────────────────────────────────────────────────────────────

const PdfFooter = ({ lessonName, downloadDate, levelLetter }: { lessonName: string; downloadDate: string; levelLetter?: string }) => (
    <View style={S.footer} fixed>
        <Text>All for One · {lessonName}{levelLetter ? ` · ${levelLetter}` : ''} · {downloadDate}</Text>
        <Text render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
            `Page ${pageNumber} of ${totalPages}`
        } />
    </View>
)

// ── Shared document header ────────────────────────────────────────────────────

const DocHeader = ({
    lesson, assignedClassName, gradeBand, subject, logoDataUrl, badgeText, badgeKey,
}: PdfBaseProps & { badgeText: string; badgeKey: 'draft' | 'answerKey' }) => (
    <View style={S.docHeader} fixed>
        <View style={S.headerLeft}>
            {logoDataUrl ? <Image src={logoDataUrl} style={S.logo} /> : null}
            <View>
                <Text style={S.lessonName}>{lesson.name}</Text>
                {assignedClassName ? <Text style={S.headerDetail}>Class: {assignedClassName}</Text> : null}
                {subject ? <Text style={S.headerDetail}>Subject: {subject}</Text> : null}
                {gradeBand ? <Text style={S.headerDetail}>Grade band: {gradeBand}</Text> : null}
            </View>
        </View>
        <Text style={badgeKey === 'draft' ? S.badgeDraft : S.badgeAnswerKey}>{badgeText}</Text>
    </View>
)

// ── Question rendering ────────────────────────────────────────────────────────

function renderMCQuestion(q: MultipleChoiceQuestion, label: string, isWorksheet: boolean, imageMap: Record<string, string>) {
    const letters = ['A', 'B', 'C', 'D']
    const img = (url: string) => imageMap[url] ?? url
    const useVertical = q.options.some(opt => opt.length > 25)
    return (
        <View style={isWorksheet ? S.questionWs : S.question} wrap={false}>
            {isWorksheet && q.imageUrl ? <Image src={img(q.imageUrl)} style={S.questionImage} /> : null}
            <View style={S.questionRow}>
                {label ? <Text style={S.questionLabel}>{label} </Text> : null}
                <Text style={S.questionText}>{q.question}</Text>
            </View>
            <View style={useVertical ? S.mcOptionsCol : S.mcOptionsRow}>
                {q.options.map((opt, i) => (
                    <View key={i} style={useVertical ? S.mcOptionFull : S.mcOptionFlex}>
                        {isWorksheet && q.optionImageUrls?.[i]
                            ? <Image src={img(q.optionImageUrls[i])} style={S.mcOptionImage} />
                            : null}
                        <View style={S.mcOptionRow}>
                            <Text style={S.mcLetter}>{letters[i]}.</Text>
                            <Text style={{ flexShrink: 1 }}>{opt}</Text>
                        </View>
                    </View>
                ))}
            </View>
        </View>
    )
}

function renderFillBlankQuestion(q: FillBlankQuestion, label: string, isWorksheet: boolean, imageMap: Record<string, string>) {
    const img = (url: string) => imageMap[url] ?? url
    return (
        <View style={isWorksheet ? S.questionWs : S.question} wrap={false}>
            {isWorksheet && q.imageUrl ? <Image src={img(q.imageUrl)} style={S.questionImage} /> : null}
            <View style={S.fillBlankRow}>
                {label ? <Text style={S.questionLabel}>{label} </Text> : null}
                {q.question.map((part, i) => (
                    <React.Fragment key={i}>
                        <Text style={S.questionText}>{part}</Text>
                        {i < q.question.length - 1 ? <View style={S.blankLine} /> : null}
                    </React.Fragment>
                ))}
            </View>
        </View>
    )
}

function renderShortAnswerQuestion(q: ShortAnswerQuestion, label: string, isWorksheet: boolean, imageMap: Record<string, string>) {
    const img = (url: string) => imageMap[url] ?? url
    return (
        <View style={isWorksheet ? S.questionWs : S.question} wrap={false}>
            {isWorksheet && q.imageUrl ? <Image src={img(q.imageUrl)} style={S.questionImage} /> : null}
            <View style={S.questionRow}>
                {label ? <Text style={S.questionLabel}>{label} </Text> : null}
                <Text style={S.questionText}>{q.question}</Text>
            </View>
            <View style={S.shortAnswerLines}>
                {[0, 1, 2, 3, 4].map(i => <View key={i} style={S.ruledLine} />)}
            </View>
        </View>
    )
}

function renderQuestion(q: Question, label: string, isWorksheet: boolean, imageMap: Record<string, string>): React.ReactNode {
    switch (q.type) {
        case 'multiple-choice':
            return renderMCQuestion(q as MultipleChoiceQuestion, label, isWorksheet, imageMap)
        case 'fill-blank':
            return renderFillBlankQuestion(q as FillBlankQuestion, label, isWorksheet, imageMap)
        case 'short-answer':
            return renderShortAnswerQuestion(q as ShortAnswerQuestion, label, isWorksheet, imageMap)
        default:
            return null
    }
}

// ── Draft / Answer Key ────────────────────────────────────────────────────────

const SECTION_MAP: Record<string, { label: string; stages: string[] }> = {
    assessment: { label: 'Pre/Post Assessment', stages: ['Pre-Assessment', 'Post-Assessment'] },
    cfu: { label: 'Check for Understanding', stages: ['Check for Understanding'] },
    iw: { label: 'Independent Work', stages: ['Independent Work'] },
}

function getSlidesForSection(questionSlides: QuestionSlide[], sectionKey: string): QuestionSlide[] {
    const stages = SECTION_MAP[sectionKey]?.stages ?? []
    const matching = (questionSlides || []).filter(qs => stages.includes(qs.learningStage))
    if (sectionKey === 'assessment' && matching.length > 1) return [matching[0]]
    return matching
}

function getAnswerText(q: Question): string {
    if (q.type === 'multiple-choice') return (q as MultipleChoiceQuestion).options[(q as MultipleChoiceQuestion).answer] ?? ''
    if (q.type === 'fill-blank') return (q as FillBlankQuestion).answer.join(', ')
    return (q as ShortAnswerQuestion).answer
}

function getQuestionText(q: Question): string {
    if (q.type === 'fill-blank') return (q as FillBlankQuestion).question.join(' _____ ')
    return (q as any).question
}

function renderDraftBody(
    lesson: Lesson,
    sectionKeys: string[],
    levels: string[],
    sortOrder: 'section-level' | 'level-section',
) {
    const renderLevelBlock = (slides: QuestionSlide[], level: string, isAssessment: boolean) => {
        const rows: { label: string; text: string; answer: string }[] = []
        for (const qs of slides) {
            for (const qbd of qs.questionsByDifficulty) {
                if (qbd.difficulty !== level) continue
                for (const q of qbd.questions) {
                    let label = (q as any).displayLabel ?? ''
                    if (isAssessment && label) {
                        const pos = label.replace('.PRE.', '.POS.')
                        if (pos !== label) label = `${label} / ${pos}`
                    }
                    rows.push({ label, text: getQuestionText(q), answer: getAnswerText(q) })
                }
            }
        }
        if (rows.length === 0) return null
        return rows.map((r, i) => (
            <View key={i} style={S.question} wrap={false}>
                <View style={S.questionRow}>
                    <Text style={S.questionLabel}>{r.label} </Text>
                    <Text style={S.questionText}>{r.text}</Text>
                </View>
                <Text style={S.answerText}>Answer: {r.answer}</Text>
            </View>
        ))
    }

    if (sortOrder === 'section-level') {
        return sectionKeys.map(key => {
            const slides = getSlidesForSection(lesson.questionSlides, key)
            if (!slides.length) return null
            return (
                <View key={key}>
                    <Text style={S.sectionHeader}>{SECTION_MAP[key].label}</Text>
                    {levels.map(level => {
                        const content = renderLevelBlock(slides, level, key === 'assessment')
                        if (!content) return null
                        return (
                            <View key={level}>
                                <Text style={S.levelHeader}>{level}</Text>
                                {content}
                            </View>
                        )
                    })}
                </View>
            )
        })
    }
    return levels.map(level => (
        <View key={level}>
            <Text style={S.sectionHeader}>{level}</Text>
            {sectionKeys.map(key => {
                const slides = getSlidesForSection(lesson.questionSlides, key)
                if (!slides.length) return null
                const content = renderLevelBlock(slides, level, key === 'assessment')
                if (!content) return null
                return (
                    <View key={key}>
                        <Text style={S.levelHeader}>{SECTION_MAP[key].label}</Text>
                        {content}
                    </View>
                )
            })}
        </View>
    ))
}

export interface DraftKeyPdfProps extends PdfBaseProps {
    selectedSections: Set<string>
    selectedLevels: Set<string>
    sortOrder: 'section-level' | 'level-section'
}

export const DraftPdfDocument = (props: DraftKeyPdfProps) => {
    const { lesson, selectedSections, selectedLevels, sortOrder, downloadDate, ...rest } = props
    const sectionKeys = ['assessment', 'cfu', 'iw'].filter(k => selectedSections.has(k))
    const levels = ['Emerging', 'Intermediate', 'Advanced'].filter(l => selectedLevels.has(l))
    return (
        <Document>
            <Page style={S.page} size="LETTER">
                <DocHeader {...rest} lesson={lesson} badgeText="DRAFT" badgeKey="draft" downloadDate={downloadDate} />
                {renderDraftBody(lesson, sectionKeys, levels, sortOrder)}
                <PdfFooter lessonName={lesson.name} downloadDate={downloadDate} />
            </Page>
        </Document>
    )
}

export const AnswerKeyPdfDocument = (props: DraftKeyPdfProps) => {
    const { lesson, selectedSections, selectedLevels, sortOrder, downloadDate, ...rest } = props
    const sectionKeys = ['assessment', 'cfu', 'iw'].filter(k => selectedSections.has(k))
    const levels = ['Emerging', 'Intermediate', 'Advanced'].filter(l => selectedLevels.has(l))
    return (
        <Document>
            <Page style={S.page} size="LETTER">
                <DocHeader {...rest} lesson={lesson} badgeText="ANSWER KEY" badgeKey="answerKey" downloadDate={downloadDate} />
                {renderDraftBody(lesson, sectionKeys, levels, sortOrder)}
                <PdfFooter lessonName={lesson.name} downloadDate={downloadDate} />
            </Page>
        </Document>
    )
}

// ── Worksheet ─────────────────────────────────────────────────────────────────

const LEVEL_LETTERS: Record<string, string> = { Emerging: 'E', Intermediate: 'I', Advanced: 'A' }
const WS_SECTION_ORDER = ['Pre-Assessment', 'Check for Understanding', 'Independent Work', 'Post-Assessment']

function getQuestionsForLevelSection(lesson: Lesson, level: string, section: string) {
    const result: { slide: number; questions: Question[] }[] = []
    for (const qs of (lesson.questionSlides || []) as QuestionSlide[]) {
        if (qs.learningStage !== section) continue
        const dg = qs.questionsByDifficulty?.find(d => d.difficulty === level)
        if (dg && dg.questions.length > 0) result.push({ slide: qs.slide, questions: dg.questions })
    }
    return result
}

export function collectWorksheetImageUrls(lesson: Lesson, levels: string[], sections: string[]): string[] {
    const urls: string[] = []
    for (const level of levels) {
        for (const section of sections) {
            for (const group of getQuestionsForLevelSection(lesson, level, section)) {
                for (const q of group.questions) {
                    if (q.imageUrl) urls.push(q.imageUrl)
                    if (q.type === 'multiple-choice') {
                        const mc = q as MultipleChoiceQuestion
                        if (mc.optionImageUrls) {
                            Object.values(mc.optionImageUrls).forEach(url => { if (url) urls.push(url) })
                        }
                    }
                }
            }
        }
    }
    return urls
}

function getWsQuestionTypes(lesson: Lesson, level: string, sections: string[]) {
    const types = new Set<string>()
    for (const s of sections)
        for (const g of getQuestionsForLevelSection(lesson, level, s))
            for (const q of g.questions) types.add(q.type)
    return types
}

const WorksheetContent = ({
    lesson, level, sections, assignedClassName, gradeBand, subject, logoDataUrl, imageMap,
}: { lesson: Lesson; level: string; sections: string[]; assignedClassName?: string; gradeBand?: string; subject?: string; logoDataUrl: string; imageMap: Record<string, string> }) => {
    const types = getWsQuestionTypes(lesson, level, sections)
    const dirLines: string[] = []
    if (types.has('multiple-choice')) dirLines.push('Circle the letter of the correct answer.')
    if (types.has('fill-blank')) dirLines.push('Write the missing word or words on the line.')
    if (types.has('short-answer')) dirLines.push('Write your answer in the lines provided.')

    return (
        <View>
            {/* Name line at top */}
            <View style={S.wsTopRow}>
                <View style={S.wsNameRow}>
                    <Text style={S.wsNameLabel}>Name:</Text>
                    <View style={S.wsNameBlank} />
                </View>
            </View>

            {/* Logo + lesson info header */}
            <View style={S.wsDocHeader}>
                <View style={S.headerLeft}>
                    {logoDataUrl ? <Image src={logoDataUrl} style={S.logo} /> : null}
                    <View>
                        <Text style={S.lessonName}>{lesson.name}</Text>
                        {assignedClassName ? <Text style={S.headerDetail}>Class: {assignedClassName}</Text> : null}
                        {subject ? <Text style={S.headerDetail}>Subject: {subject}</Text> : null}
                        {gradeBand ? <Text style={S.headerDetail}>Grade band: {gradeBand}</Text> : null}
                    </View>
                </View>
            </View>

            {/* Directions */}
            {dirLines.length > 0 && (
                <View style={S.directions}>
                    {dirLines.map((l, i) => <Text key={i} style={S.directionLine}>{l}</Text>)}
                </View>
            )}

            {/* Sections */}
            {WS_SECTION_ORDER.filter(s => sections.includes(s)).map(section => {
                const groups = getQuestionsForLevelSection(lesson, level, section)
                if (groups.length === 0) return null
                const isCfu = section === 'Check for Understanding'
                let pos = 0
                return (
                    <View key={section}>
                        <Text style={S.sectionHeader}>{section}</Text>
                        {groups.map(group =>
                            group.questions.map(q => {
                                pos++
                                const label = isCfu ? '' : `${pos}.`
                                return (
                                    <View key={q.id}>
                                        {isCfu ? <Text style={S.slideNote}>Answer after slide {group.slide + 1}</Text> : null}
                                        {renderQuestion(q, label, true, imageMap)}
                                    </View>
                                )
                            })
                        )}
                    </View>
                )
            })}
        </View>
    )
}

export interface WorksheetPdfProps extends PdfBaseProps {
    worksheets: WorksheetEntry[]
    level: string
    imageMap: Record<string, string>
}

export const WorksheetPdfDocument = ({
    lesson, assignedClassName, gradeBand, subject, logoDataUrl, downloadDate, worksheets, level, imageMap,
}: WorksheetPdfProps) => (
    <Document>
        <Page style={S.page} size="LETTER">
            {worksheets.map((ws, i) => (
                <View key={i} break={i > 0}>
                    <WorksheetContent
                        lesson={lesson}
                        level={ws.level}
                        sections={ws.sections}
                        assignedClassName={assignedClassName}
                        gradeBand={gradeBand}
                        subject={subject}
                        logoDataUrl={logoDataUrl}
                        imageMap={imageMap}
                    />
                </View>
            ))}
            <PdfFooter lessonName={lesson.name} downloadDate={downloadDate} levelLetter={LEVEL_LETTERS[level]} />
        </Page>
    </Document>
)
